import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import { TableCell, TableRow } from "@/components/ui/table";
import { type Expense } from "@/hooks/useExpenses";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import { formatFullNumericDateUTC } from "@/utils/shared/dateUtils";
import { truncateText } from "@/utils/shared/stringUtils";
import { Edit, Eye, Trash2, CircleCheck, Clock, CalendarClock } from "lucide-react";

const getExpenseStatusBadge = (status: string) => {
  switch (status) {
    case "Pagado":
      return {
        icon: <CircleCheck className="w-3 h-3" />,
        className: "bg-green-100 text-green-800 border-green-300"
      };
    case "Programado":
      return {
        icon: <CalendarClock className="w-3 h-3" />,
        className: "bg-blue-100 text-blue-800 border-blue-300"
      };
    case "Pendiente":
      return {
        icon: <Clock className="w-3 h-3" />,
        className: "bg-yellow-100 text-yellow-800 border-yellow-300"
      };
    default:
      return {
        icon: null,
        className: ""
      };
  }
};

interface ExpenseRowProps {
  expense: Expense;
  index: number;
  onViewReceipt: (url: string) => void;
  onEdit: (expense: Expense) => void;
  onDelete: (expense: Expense) => void;
  canEdit: boolean;
  canDelete: boolean;
  isSelected: boolean;
  onSelect: (expenseId: string, index: number, shiftKey: boolean) => void;
}

const ExpenseRow = ({
  expense,
  index,
  onEdit,
  onDelete,
  onViewReceipt,
  canEdit,
  canDelete,
  isSelected,
  onSelect,
}: ExpenseRowProps) => {
  return (
    <TableRow>
      <TableCell onClick={(e) => onSelect(expense.id, index, e.shiftKey)}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => {}}
          aria-label={`Seleccionar gasto ${expense.id}`}
        />
      </TableCell>
      <TableCell>{formatFullNumericDateUTC(expense.expense_date)}</TableCell>

      <TableCell>
        <span className="font-medium" title={expense.properties?.title}>
          {truncateText(expense.properties?.short_name || "", 15)}
        </span>
      </TableCell>

      <TableCell>{expense.category?.name}</TableCell>

      <TableCell>{expense.subcategory?.name}</TableCell>

      <TableCell className="max-w-[200px]">
        <div className="whitespace-pre-wrap break-words">
          {expense.description || "-"}
        </div>
      </TableCell>

      <TableCell>{formatCurrency(expense.amount)}</TableCell>

      <TableCell>
        {(() => {
          const statusConfig = getExpenseStatusBadge(expense.status);
          return (
            <Badge variant="outline" className={`flex items-center gap-1 w-fit ${statusConfig.className}`}>
              {statusConfig.icon}
              <span>{expense.status}</span>
            </Badge>
          );
        })()}
      </TableCell>

      <TableCell>{expense.payment_method}</TableCell>

      <TableCell className="whitespace-nowrap">
        {expense.expense_tags ? (
          <Badge
            style={{
              backgroundColor: expense.expense_tags.color,
              color: "#ffffff",
            }}
            className="text-xs"
          >
            {expense.expense_tags.name}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-xs">Sin etiqueta</span>
        )}
      </TableCell>

      <TableCell>
        {expense.receipt_url ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewReceipt(expense.receipt_url!)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>

      {(canEdit || canDelete) && (
        <TableCell className="flex items-center space-x-2">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(expense)}>
              <Edit className="h-4 w-4" />
            </Button>
          )}

          {canDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(expense)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export default ExpenseRow;
